import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy, where, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface Announcement {
    id: string;
    title: string;
    content: string;
    category: '공지' | '점검' | '상장' | '이벤트' | '보안';
    priority: 'high' | 'normal' | 'low';
    isActive: boolean;
    createdAt: Timestamp;
    updatedAt: Timestamp;
    author: string;
}

export const useAnnouncements = (activeOnly = true) => {
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let q = query(
            collection(db, 'announcements'),
            orderBy('createdAt', 'desc')
        );

        if (activeOnly) {
            q = query(
                collection(db, 'announcements'),
                where('isActive', '==', true),
                orderBy('createdAt', 'desc')
            );
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Announcement[];
            setAnnouncements(data);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching announcements:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [activeOnly]);

    const createAnnouncement = async (data: Omit<Announcement, 'id' | 'createdAt' | 'updatedAt'>) => {
        try {
            await addDoc(collection(db, 'announcements'), {
                ...data,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
            return { success: true };
        } catch (error) {
            console.error('Error creating announcement:', error);
            return { success: false, error };
        }
    };

    const updateAnnouncement = async (id: string, data: Partial<Omit<Announcement, 'id' | 'createdAt'>>) => {
        try {
            await updateDoc(doc(db, 'announcements', id), {
                ...data,
                updatedAt: Timestamp.now()
            });
            return { success: true };
        } catch (error) {
            console.error('Error updating announcement:', error);
            return { success: false, error };
        }
    };

    const deleteAnnouncement = async (id: string) => {
        try {
            await deleteDoc(doc(db, 'announcements', id));
            return { success: true };
        } catch (error) {
            console.error('Error deleting announcement:', error);
            return { success: false, error };
        }
    };

    return {
        announcements,
        loading,
        createAnnouncement,
        updateAnnouncement,
        deleteAnnouncement
    };
};
